import { Component, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { ToastService } from '../toast.service';
import { environment } from '../../environments/environment';

declare const lucide: any;

function passwordMatchValidator(control: AbstractControl) {
  const parent = control.parent;
  if (!parent) return null;
  const newPassword = parent.get('newPassword')?.value;
  return control.value === newPassword ? null : { mismatch: true };
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SidebarComponent],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, AfterViewInit {
  activeTab = 'security';

  userEmail = '';
  userRole = '';
  userType = '';

  passwordForm!: FormGroup;
  isSubmitting = false;

  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  passwordStrength = 0;
  passwordStrengthLabel = '';
  passwordStrengthClass = '';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadUserInfo();
    this.passwordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]*/)
      ]],
      confirmPassword: ['', [Validators.required, passwordMatchValidator]]
    });

    this.passwordForm.get('newPassword')?.valueChanges.subscribe(val => {
      this.updatePasswordStrength(val);
      // Re-validate confirmPassword when newPassword changes
      this.passwordForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  ngAfterViewInit() {
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  loadUserInfo() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.userEmail = user.email || '';
        this.userRole = user.user_type || '';
        this.userType = user.user_type?.toLowerCase() || '';
      } catch {}
    }
  }

  updatePasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = 0;
      this.passwordStrengthLabel = '';
      this.passwordStrengthClass = '';
      return;
    }

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;

    this.passwordStrength = Math.min(Math.round((score / 6) * 100), 100);

    if (score <= 2) {
      this.passwordStrengthLabel = 'Weak';
      this.passwordStrengthClass = 'weak';
    } else if (score <= 4) {
      this.passwordStrengthLabel = 'Fair';
      this.passwordStrengthClass = 'fair';
    } else if (score === 5) {
      this.passwordStrengthLabel = 'Good';
      this.passwordStrengthClass = 'good';
    } else {
      this.passwordStrengthLabel = 'Strong';
      this.passwordStrengthClass = 'strong';
    }
  }

  hasUppercase(val: string): boolean {
    return /[A-Z]/.test(val || '');
  }

  hasNumber(val: string): boolean {
    return /\d/.test(val || '');
  }

  hasSpecialChar(val: string): boolean {
    return /[@$!%*?&]/.test(val || '');
  }

  onChangePassword() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const { currentPassword, newPassword, confirmPassword } = this.passwordForm.value;

    this.http.post<any>(`${environment.apiBaseUrl}/auth/change-password`, {
      currentPassword,
      newPassword,
      confirmPassword
    }).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.toastService.success('Password changed successfully!');
        this.passwordForm.reset();
        this.passwordStrength = 0;
        this.passwordStrengthLabel = '';
        this.passwordStrengthClass = '';
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg = err?.error?.message || 'Failed to change password. Please try again.';
        this.toastService.error(msg);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const control = this.passwordForm.get(fieldName);
    if (!control || !control.touched || !control.errors) return '';
    if (control.errors['required']) return `${this.getLabel(fieldName)} is required`;
    if (control.errors['minlength']) return 'Password must be at least 8 characters';
    if (control.errors['pattern']) return 'Must contain uppercase, lowercase, number & special character';
    if (control.errors['mismatch']) return 'Passwords do not match';
    return '';
  }

  private getLabel(field: string): string {
    const labels: Record<string, string> = {
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password'
    };
    return labels[field] || field;
  }
}
